import { openDB, type IDBPDatabase } from 'idb';
import { isPrunable, type OutboxEntry } from '@/domain';
import type { EnqueueRequest, OutboxQueue } from '@/application/ports/OutboxQueue';
import { requestBackgroundSync } from './backgroundSync';

const DB_NAME = 'ceramica-outbox';
const DB_VERSION = 1;
const STORE = 'entries';

/**
 * The outbox, in IndexedDB. Section 9.
 *
 * IndexedDB rather than localStorage because the entries must survive a tab
 * crash and a phone running out of memory mid-shift, and because a photo
 * payload is a Blob that localStorage cannot hold at all.
 *
 * Every write notifies subscribers before it resolves, so the tray count in the
 * top bar is never behind what the database holds.
 */
export class IdbOutboxQueue implements OutboxQueue {
  private db: Promise<IDBPDatabase> | null = null;
  private readonly listeners = new Set<() => void>();

  private open(): Promise<IDBPDatabase> {
    this.db ??= openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        // Replay reads by status and orders within a kit; both are indexed so a
        // shift's worth of entries does not become a full scan on every tick.
        store.createIndex('status', 'status');
        store.createIndex('kitId', 'kitId');
      },
    });
    return this.db;
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  async enqueue(request: EnqueueRequest): Promise<OutboxEntry> {
    const entry: OutboxEntry = {
      ...request,
      attempts: 0,
      lastError: null,
      status: 'pending',
    };

    const db = await this.open();
    await db.put(STORE, entry);
    this.notify();

    /*
     * Ask for a background replay as soon as the command is durable, not when
     * the send fails. A phone killed between the tap and the response never
     * reaches the failure path, and that is precisely the case worth covering.
     *
     * Registering the same tag twice is a no-op, so this costs nothing on the
     * commands that go straight through. Not awaited: whether the browser
     * accepts the registration has no bearing on whether the work is saved.
     */
    void requestBackgroundSync();

    return entry;
  }

  async all(): Promise<OutboxEntry[]> {
    const db = await this.open();
    return (await db.getAll(STORE)) as OutboxEntry[];
  }

  async markSent(id: string): Promise<void> {
    await this.patch(id, (entry) => ({ ...entry, status: 'sent', lastError: null }));
  }

  /**
   * A retryable failure stays pending so the next reconnect picks it up. A
   * refused one becomes `failed` and waits in the tray. Either way the reason
   * is recorded, because "it did not sync" without a reason is not something a
   * manager can act on. Section 9 rule 4.
   */
  async markFailed(id: string, error: string, retryable: boolean): Promise<void> {
    await this.patch(id, (entry) => ({
      ...entry,
      attempts: entry.attempts + 1,
      lastError: error,
      status: retryable ? 'pending' : 'failed',
    }));
  }

  async discard(id: string): Promise<void> {
    const db = await this.open();
    await db.delete(STORE, id);
    this.notify();
  }

  async prune(now: number): Promise<void> {
    const db = await this.open();
    const stale = ((await db.getAll(STORE)) as OutboxEntry[]).filter((entry) =>
      isPrunable(entry, now),
    );
    if (stale.length === 0) return;

    const tx = db.transaction(STORE, 'readwrite');
    await Promise.all([...stale.map((entry) => tx.store.delete(entry.id)), tx.done]);
    this.notify();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private async patch(
    id: string,
    change: (entry: OutboxEntry) => OutboxEntry,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE, 'readwrite');
    const existing = (await tx.store.get(id)) as OutboxEntry | undefined;
    // A replay racing a prune can find the entry already gone. That is not an
    // error worth surfacing: the work reached the server either way.
    if (existing !== undefined) await tx.store.put(change(existing));
    await tx.done;
    this.notify();
  }
}
